import * as yup from "yup";
import { IntlShape } from 'react-intl';
import { RegistrationFlowType } from "myParkdUtils/enums";
import { Sector } from 'screens/registration/InputSector';


// INTL
// It was required to define the validationSchema as a function that receives intl, 
// as otherwise it lives outside the react lifecycle and was imported, before intl was initialized.

// YUP
// for some reason when using Yup's when() context variables accessed with $ are wrapped in an array
// and need to be accessed with myVar[0]. This is not mentioned in the documentation.

export type RegistrationSchema = 'STEP_COMPANY' | 'STEP_ACCOUNT' | 'STEP_PAYMENT';

type CompanyFormData = yup.InferType<ReturnType<typeof companyDetailsSchema>>;
type AccountFormData = yup.InferType<ReturnType<typeof accountDetailsSchema>>;
type PaymentFormData = yup.InferType<ReturnType<typeof paymentDetailsSchema>>;

export type FormRegistrationData =
    CompanyFormData &
    AccountFormData &
    PaymentFormData;


export const getValidationSchema = (intl: IntlShape, schema: RegistrationSchema) => {
    switch (schema) {
        case 'STEP_COMPANY':
            return companyDetailsSchema(intl);
        case 'STEP_ACCOUNT':
            return accountDetailsSchema(intl);
        case 'STEP_PAYMENT':
            return paymentDetailsSchema();
    }
}

const companyDetailsSchema = (intl: IntlShape) => {
    const { formatMessage } = intl;

    return yup.object({
        vat: yup.string()
            .required(formatMessage({ id: 'VALIDATION_FIELD_IS_REQUIRED' }))
            .min(8, formatMessage({ id: 'VALIDATION_MIN_CHARACTERS' }, { minLength: 8 }))
            .test(
                "stars-with-country-code",
                formatMessage({ id: 'VALIDATION_START_WITH_TWO_LETTERS' }),
                (value) => {
                    if (!value) return false;
                    return /^[A-Z]{2}/.test(value) // Check if first 2 characters are letters
                })
            .test("api-error-status-400", formatMessage({ id: 'VALIDATION_INVALID_VAT_NUMBER' }), (value, context) => {
                const status = context?.options?.context?.vatApiErrorStatus;
                return status === undefined || status !== 400;
            })
            .test("api-error-status-500", formatMessage({ id: 'VALIDATION_VAT_API_ERROR' }), (value, context) => {
                const status = context?.options?.context?.vatApiErrorStatus;
                return status === undefined || status !== 500;
            }),

        companyName: yup.string()
            .required(formatMessage({ id: 'VALIDATION_FIELD_IS_REQUIRED' })),
        addressStreet: yup.string()
            .required(formatMessage({ id: 'VALIDATION_FIELD_IS_REQUIRED' })),
        addressNumber: yup.string()
            .required(formatMessage({ id: 'VALIDATION_FIELD_IS_REQUIRED' })),
        addressCity: yup.string()
            .required(formatMessage({ id: 'VALIDATION_FIELD_IS_REQUIRED' })),
        addressPostalCode: yup.string()
            .required(formatMessage({ id: 'VALIDATION_FIELD_IS_REQUIRED' })),
        addressCountry: yup.string()
            .required(formatMessage({ id: 'VALIDATION_FIELD_IS_REQUIRED' })),
        sector:
            yup.mixed<Sector>()
                .nullable()
                .required(formatMessage({ id: 'VALIDATION_FIELD_IS_REQUIRED' })),
        invoicingEmailAddress: yup.string()
            .email(formatMessage({ id: 'VALIDATION_INVALID_EMAIL' }))
    })
}


const accountDetailsSchema = (intl: IntlShape) => {
    const { formatMessage } = intl;

    return yup.object({
        // ACCOUNT
        firstName: yup.string()
            .required(formatMessage({ id: 'VALIDATION_FIELD_IS_REQUIRED' })),
        lastName: yup.string()
            .required(formatMessage({ id: 'VALIDATION_FIELD_IS_REQUIRED' })),
        phoneNumber: yup.string()
            .required(formatMessage({ id: 'VALIDATION_FIELD_IS_REQUIRED' }))
            .test("valid-phone-number", formatMessage({ id: 'VALIDATION_INVALID_PHONENUMBER' }), (value, context) => {
                return context?.options?.context?.validPhone === true
            }),
        emailAddress: yup.string()
            .required(formatMessage({ id: 'VALIDATION_FIELD_IS_REQUIRED' }))
            .email(formatMessage({ id: 'VALIDATION_INVALID_EMAIL' }))
            .test("email-exists", formatMessage({ id: 'PARKD_API_ERROR_EMAIL_ALREADY_EXISTS' }), (value, context) => {
                return context?.options.context?.emailExists === false
            }),
        password: yup.string()
            .min(8, formatMessage({ id: 'VALIDATION_PASSWORD_MIN_8_CHARACTERS' }))
            .matches(/[A-Z]/, String(formatMessage({ id: 'VALIDATION_PASSWORD_UPPERCASE_LETTER' })))
            .matches(/[a-z]/, String(formatMessage({ id: 'VALIDATION_PASSWORD_LOWERCASE_LETTER' })))
            .matches(/\d/, String(formatMessage({ id: 'VALIDATION_PASSWORD_NUMBER' })))
            .matches(/[^A-Za-z0-9]/, String(formatMessage({ id: 'VALIDATION_PASSWORD_SPECIAL_CHARACTER' }))) // Allow everything non-alphanumeric? Or use custom set of characters?
            //.matches(/[!@#$%^&*]/, String(formatMessage({ id: 'VALIDATION_PASSWORD_SPECIAL_CHARACTER' })))
            .matches(/^\S*$/, String(formatMessage({ id: 'VALIDATION_PASSWORD_NO_SPACES' })))
            .required(String(formatMessage({ id: 'VALIDATION_FIELD_IS_REQUIRED' })))
        ,

        // FLEET / CAR(oem) (conditional fields) ------------------------------------------

        telematicProvider: yup
            .string()
            .nullable()
            .notRequired()
            .when('$flowType', (flowType, schema) => {
                return flowType[0] === RegistrationFlowType.OEM
                    ? schema
                    : schema
                        .required(formatMessage({ id: 'VALIDATION_FIELD_IS_REQUIRED' }))
            })
        ,
        otherTelematicProvider: yup
            .string()
            .nullable()
            .notRequired()
            .when('telematicProvider', (telematicProvider, schema) => {
                return telematicProvider[0] === 'OTHER'
                    ? schema
                        .required(formatMessage({ id: 'VALIDATION_FIELD_IS_REQUIRED' }))
                    : schema;
            }),
        licensePlateCountryCode: yup
            .string(),
        licensePlate: yup
            .string()
            .nullable()
            .notRequired()
            .when('$flowType', (flowType, schema) => {
                return flowType[0] === RegistrationFlowType.OEM
                    ? schema
                        .required(formatMessage({ id: 'VALIDATION_FIELD_IS_REQUIRED' }))
                    : schema;
            }),
        vin: yup
            .string()
            .nullable()
            .notRequired()
            .when('$flowType', (flowType, schema) => {
                return flowType[0] === RegistrationFlowType.OEM
                    ? schema.test(
                        'vin-length',
                        formatMessage({ id: 'VALIDATION_VIN_17_CHARACTERS' }),
                        (value) => {
                            if (!value) return true;
                            return value.length === 17;
                        }
                    )
                    : schema;
            })
        ,
        numCars: yup
            .number()
            .transform((value, originalValue) => (originalValue === '' ? undefined : value))
            .required(formatMessage({ id: 'VALIDATION_FIELD_IS_REQUIRED' }))
        ,
        acceptedTC: yup
            .boolean()
            .required()
            .oneOf([true], formatMessage({ id: 'VALIDATION_CHECKBOX_NOT_ACCEPTED' }))
        ,
    })
}


const paymentDetailsSchema = () => {
    return yup.object({
        mandateAcceptedByUser: yup
            .boolean()
            .required()
        ,
        paymentType: yup.string()
    });
}
